/**
 * The sequencer — patterns, scenes and the song arrangement.
 *
 * The grid writes to the model and nothing else; the sound comes from the look-ahead
 * scheduler in `state.ts`, which reads the same model on the audio clock. That separation is
 * why editing a step while the pattern is running never produces a click: the edit lands in
 * the data, and the next pass picks it up.
 */

import { disposeTree, h, list, on, onCleanup } from '../../core/dom';
import { computed, effect, signal, untracked } from '../../core/signal';
import { chip, icon, iconButton, neonButton, segmentedRail, toggle, valueStepper } from '../components/controls';
import { confirmDialog, emptyState } from '../components/sheet';
import {
  PATTERN_LENGTH_OPTIONS,
  loopBarLength,
  makePattern,
  makeScene,
  makeSongSection,
  makeTrack,
  newUUID,
  padIsEmpty,
  patternLengthInBeats,
  projectAsset,
  projectLoopAssets,
  projectPad,
  projectPadPlaysLoop,
  stepHasDetail,
  trackLeadingStepIndex,
  type Pad,
  type Pattern,
  type Scene,
  type Step,
  type Track,
} from '../../model/types';
import {
  bufferForAsset,
  currentPattern,
  currentPatternID,
  engine,
  mutate,
  notify,
  notifyUndoable,
  offStepValue,
  project,
  reportFailure,
  revision,
  saveRenderedAsAsset,
  sequencer,
  settings,
  stepClipboard,
  updateSettings,
} from '../../app/state';
import { renderPattern } from '../../audio/offline-render';
import { navigate } from '../../app/router';
import { presentMenu, presentPrompt, requiresProject, screenBody, screenHeader, section, type MenuItem } from '../../app/shell';
import { presentLoopSlicer } from '../sheets/loop-slicer';

/**
 * Captures a scene from what is playing right now.
 *
 * A scene is a pattern *plus* whatever is being held over the top of it — the looping bed a
 * performer keeps running while the drums change underneath. `heldPadIDs` has been in the
 * model all along and was only ever the empty array it is created with, which made every
 * scene a pattern shortcut. Reading the engine's live voices is what turns it into a scene.
 */
function captureScene(): void {
  const open = project.peek();
  if (open === null) return;
  const held = open.banks
    .flatMap((bank) => bank.pads)
    .filter((pad) => !padIsEmpty(pad) && engine.isLooping(pad.id))
    .map((pad) => pad.id);
  presentPrompt({
    title: 'Scene name',
    value: `Scene ${open.scenes.length + 1}`,
    onConfirm: (name) =>
      mutate('Add Scene', (target) => {
        const patternID = currentPattern.peek()?.id;
        target.scenes.push(
          makeScene(name, {
            ...(patternID === undefined ? {} : { patternID }),
            heldPadIDs: held,
          }),
        );
      }),
  });
  if (held.length > 0) notify('Scene captured', `${held.length} looping pad${held.length === 1 ? '' : 's'} held with it.`);
}

function sceneMenu(scene: Scene): void {
  presentMenu(scene.name, [
    {
      title: 'Launch now',
      symbol: 'play.fill',
      onSelect: () => sequencer.launchScene(scene),
    },
    {
      title: 'Rename',
      symbol: 'mouth.fill',
      onSelect: () =>
        presentPrompt({
          title: 'Scene name',
          value: scene.name,
          onConfirm: (name) =>
            mutate('Rename Scene', (open) => {
              const live = open.scenes.find((entry) => entry.id === scene.id);
              if (live !== undefined) live.name = name;
            }),
        }),
    },
    {
      title: 'Re-capture held pads',
      symbol: 'record.circle.fill',
      detail: 'Stores the pads looping right now with this scene',
      onSelect: () => {
        const open = project.peek();
        if (open === null) return;
        const held = open.banks
          .flatMap((bank) => bank.pads)
          .filter((pad) => !padIsEmpty(pad) && engine.isLooping(pad.id))
          .map((pad) => pad.id);
        mutate('Scene Held Pads', (target) => {
          const live = target.scenes.find((entry) => entry.id === scene.id);
          if (live !== undefined) live.heldPadIDs = held;
        });
        notify('Scene updated', `${held.length} held pad${held.length === 1 ? '' : 's'}.`);
      },
    },
    {
      title: 'Delete scene',
      symbol: 'trash.fill',
      destructive: true,
      onSelect: () => {
        mutate('Delete Scene', (open) => {
          open.scenes = open.scenes.filter((entry) => entry.id !== scene.id);
        });
        notifyUndoable('Scene deleted');
      },
    },
  ]);
}

function editPattern(label: string, change: (pattern: Pattern) => void, coalesceKey?: string): void {
  const id = currentPattern.peek()?.id;
  if (id === undefined) return;
  mutate(
    label,
    (open) => {
      const live = open.patterns.find((entry) => entry.id === id);
      if (live !== undefined) change(live);
    },
    coalesceKey === undefined ? {} : { coalesceKey },
  );
}

function stepInspector(trackID: string, index: number, step: Step): void {
  // Spent on opening, not on closing, so an inspector opened and abandoned still counts
  // against the three. Any completed adjustment retires the label for good.
  const hintsLeft = settings.peek().stepDragHintsLeft;
  if (hintsLeft > 0) updateSettings({ stepDragHintsLeft: hintsLeft - 1 });
  const retireHint = (): void => {
    if (settings.peek().stepDragHintsLeft !== 0) updateSettings({ stepDragHintsLeft: 0 });
  };

  /**
   * One drag is one undo entry.
   *
   * A drag writes the model on every frame, so without a coalesce key a two second adjustment
   * would bury the user's history under a hundred identical entries. The key names the
   * parameter and the step, so two different values stay separately undoable.
   */
  const write = (label: string, key: string, change: (target: Step) => void): void =>
    editPattern(
      label,
      (pattern) => {
        const target = pattern.tracks.find((entry) => entry.id === trackID)?.steps[index];
        if (target !== undefined) change(target);
      },
      `step-${key}-${trackID}-${index}`,
    );

  // One teaching label, on the first value row. Five of them would shout.
  let hinted = false;
  const valueRow = (options: {
    title: string;
    symbol: string;
    label: string;
    key: string;
    initial: number;
    min: number;
    max: number;
    step: number;
    format: (value: number) => string;
    apply: (target: Step, value: number) => void;
  }): MenuItem => {
    const hint = hintsLeft > 0 && !hinted;
    if (hint) hinted = true;
    return {
      title: options.title,
      symbol: options.symbol,
      value: {
        initial: options.initial,
        min: options.min,
        max: options.max,
        step: options.step,
        format: options.format,
        hint,
        onChange: (next) => write(options.label, options.key, (target) => options.apply(target, next)),
        onFirstAdjust: retireHint,
      },
    };
  };

  const percent = (value: number): string => `${Math.round(value * 100)}%`;
  const signedPercent = (value: number): string => `${value > 0 ? '+' : ''}${Math.round(value * 100)}%`;
  const hundredths = (value: number): number => Math.round(value * 100) / 100;

  presentMenu(`Step ${index + 1}`, [
    valueRow({
      title: 'Velocity',
      symbol: 'chart.bar.fill',
      label: 'Step Velocity',
      key: 'velocity',
      initial: step.velocity,
      min: 0.1,
      max: 1,
      step: 0.01,
      format: percent,
      apply: (target, value) => {
        target.velocity = hundredths(value);
      },
    }),
    valueRow({
      title: 'Probability',
      symbol: 'circle.circle.fill',
      label: 'Step Probability',
      key: 'probability',
      initial: step.probability,
      min: 0.1,
      max: 1,
      step: 0.01,
      format: percent,
      apply: (target, value) => {
        target.probability = hundredths(value);
      },
    }),
    valueRow({
      title: 'Pitch',
      symbol: 'arrow.up.arrow.down',
      label: 'Step Pitch',
      key: 'pitch',
      initial: step.pitchOffset,
      min: -12,
      max: 12,
      step: 1,
      format: (value) => `${value > 0 ? '+' : ''}${Math.round(value)} st`,
      apply: (target, value) => {
        target.pitchOffset = Math.round(value);
      },
    }),
    valueRow({
      title: 'Micro-timing',
      symbol: 'clock.fill',
      label: 'Step Micro-timing',
      key: 'micro',
      initial: step.microTiming,
      min: -0.25,
      max: 0.25,
      step: 0.01,
      format: signedPercent,
      apply: (target, value) => {
        target.microTiming = hundredths(value);
      },
    }),
    valueRow({
      title: 'Ratchets',
      symbol: 'rectangle.split.3x1.fill',
      label: 'Step Ratchets',
      key: 'ratchets',
      initial: step.ratchets,
      min: 1,
      max: 4,
      step: 1,
      format: (value) => String(Math.round(value)),
      apply: (target, value) => {
        target.ratchets = Math.round(value);
      },
    }),
    {
      title: step.chokesPrevious ? 'Stop choking the previous hit' : 'Choke the previous hit',
      symbol: 'door.left.hand.closed',
      onSelect: () =>
        editPattern('Step Choke', (pattern) => {
          const target = pattern.tracks.find((entry) => entry.id === trackID)?.steps[index];
          if (target !== undefined) target.chokesPrevious = !target.chokesPrevious;
        }),
    },
    {
      title: 'Copy this step',
      symbol: 'square.grid.3x3.fill',
      onSelect: () => {
        stepClipboard.value = [structuredClone(step)];
        notify('Step copied');
      },
    },
    {
      title: 'Paste here',
      symbol: 'square.and.arrow.up',
      disabled: stepClipboard.peek() === null,
      onSelect: () => {
        const copied = stepClipboard.peek()?.[0];
        if (copied === undefined) return;
        editPattern('Paste Step', (pattern) => {
          const track = pattern.tracks.find((entry) => entry.id === trackID);
          if (track !== undefined) track.steps[index] = structuredClone(copied);
        });
      },
    },
    {
      title: 'Reset this step',
      symbol: 'arrow.counterclockwise',
      destructive: true,
      onSelect: () =>
        editPattern('Reset Step', (pattern) => {
          const track = pattern.tracks.find((entry) => entry.id === trackID);
          if (track !== undefined) track.steps[index] = offStepValue();
        }),
    },
  ]);
}

/**
 * What a track's row has to say about the sound it is bound to.
 *
 * A loop is a bed: it spans the pattern rather than sitting in a step, so its row is not the
 * same instrument as a kick's row and must not pretend to be. This is the one place that
 * difference is worked out, and both the label and the grid's behaviour read it.
 */
interface TrackLoopFacts {
  /** The pad's sample is a loop, by the app's one shared test. */
  readonly padIsLoop: boolean;
  /** The track fires once per pass, from its earliest lit step. */
  readonly oneHit: boolean;
  /** Length of the sound in bars of this project's grid, 0 when unknown. */
  readonly bars: number;
  /** Bars of one pass of the pattern. */
  readonly patternBars: number;
  /** Lit steps that will never sound because the track fires once per pass. */
  readonly inertSteps: number;
}

function trackLoopFacts(track: Track, pattern: Pattern, pad: Pad | undefined): TrackLoopFacts {
  const open = project.peek();
  const padIsLoop = open !== null && pad !== undefined && projectPadPlaysLoop(open, pad);
  const asset = open === null || pad?.assetID === undefined ? undefined : projectAsset(open, pad.assetID);
  const bars =
    open === null || asset === undefined
      ? 0
      : loopBarLength(asset, open.tempo, open.timeSignatureBeats, pad?.edit.playbackRate ?? 1);
  const patternBars = open === null ? 0 : patternLengthInBeats(pattern) / Math.max(1, open.timeSignatureBeats);
  const oneHit = track.oneHitPerPass === true;
  const lit = track.steps.slice(0, track.length).filter((step) => step.isActive).length;
  return { padIsLoop, oneHit, bars, patternBars, inertSteps: oneHit ? Math.max(0, lit - 1) : 0 };
}

function formatBars(bars: number): string {
  const rounded = bars >= 1 ? Math.round(bars * 10) / 10 : Math.round(bars * 100) / 100;
  return `${rounded} bar${rounded === 1 ? '' : 's'}`;
}

/**
 * The sentence under the track name.
 *
 * Silent for an ordinary track: a kick needs no explaining. The column is 103px wide in
 * portrait and 83px in landscape, at 10px, clamped to two lines, so the budget is 30
 * characters and not one more. The strings this replaces were 47 and 48 characters and were
 * truncated in both orientations at both type scales. "4.6 bars bed · once per pass ·
 * pattern i…" is not an explanation, it is a stub. The fact worth the room is that the track
 * fires once per pass. How long a pass is, is on the bar ruler above the grid, and the whole
 * sentence is on the Add track menu, where the choice is actually made.
 */
function trackNote(facts: TrackLoopFacts): string {
  if (facts.oneHit) return facts.bars > 0 ? `Once per pass · ${formatBars(facts.bars)}` : 'Once per pass';
  if (facts.padIsLoop) return 'Every lit step: copies overlap';
  return '';
}

/**
 * The bar ruler that rides with the step columns.
 *
 * It is a row of the same grid as every lane, built from the same `pattern.length` and living
 * inside the same single scroller, so it cannot come apart from the columns it labels: ruler
 * column 5 is step 5 on every track, at the same x, at every scroll position.
 */
interface StepRulerHandle {
  readonly el: HTMLElement;
  sync(pattern: Pattern): void;
}

function stepRuler(pattern: Pattern): StepRulerHandle {
  const cells: HTMLElement[] = [];
  const strip = h('div', { class: 'seq-cells seq-ruler-cells' });
  // Decoration. Every step button already announces its own number, and 64 spare nodes in the
  // rotor would be 64 more things to swipe past.
  const el = h('div', { class: 'seq-ruler', 'aria-hidden': 'true' }, h('div', { class: 'seq-ruler-head' }), strip);

  const paint = (live: Pattern): void => {
    const beatsPerBar = Math.max(1, project.peek()?.timeSignatureBeats ?? 4);
    const stepsPerBar = Math.max(1, live.stepsPerBeat * beatsPerBar);
    while (cells.length > live.length) cells.pop()?.remove();
    while (cells.length < live.length) {
      const cell = h('span', { class: 'seq-ruler-cell' });
      cells.push(cell);
      strip.appendChild(cell);
    }
    for (let index = 0; index < cells.length; index++) {
      const cell = cells[index]!;
      const startsBar = index % stepsPerBar === 0;
      // A number on the first step of each bar, a dot on the first step of each beat, nothing
      // in between: enough to count bars by, quiet enough to ignore.
      cell.classList.toggle('seq-ruler-cell--bar', startsBar);
      cell.textContent = startsBar ? String(index / stepsPerBar + 1) : index % live.stepsPerBeat === 0 ? '·' : '';
    }
  };

  paint(pattern);
  return { el, sync: paint };
}

/**
 * A track's row, built once and kept for as long as the track exists.
 *
 * Rebuilding the list on every edit threw away more than nodes: the shared scroller collapses
 * to zero width while it is empty, so a user editing bar 4 of a 64 step pattern was thrown back
 * to bar 1 by their own edit, and the discarded rows kept their effects running because
 * `replaceChildren` does not release bindings. A row is now created once, re-pointed by `sync`
 * on every edit, and torn down only when its track is genuinely gone.
 */
interface TrackRowHandle {
  readonly el: HTMLElement;
  /** Re-points the row at the live track and pattern. Keeps every node it already has. */
  sync(track: Track, pattern: Pattern): void;
  /** Stops the row's effect and releases its listeners. */
  destroy(): void;
}

function trackRow(firstTrack: Track, firstPattern: Pattern): TrackRowHandle {
  // Undo swaps the whole document for a fresh clone, so a row cannot hold one Track object for
  // ever. These are the current ones and `sync` replaces them.
  let track = firstTrack;
  let pattern = firstPattern;

  const boundPad = (): Pad | undefined => {
    const open = project.peek();
    return open === null || track.padID === undefined ? undefined : projectPad(open, track.padID);
  };
  let facts = trackLoopFacts(track, pattern, boundPad());

  const cells: HTMLButtonElement[] = [];
  const grid = h('div', { class: 'seq-cells instrument' });

  const makeCell = (index: number): HTMLButtonElement =>
    h('button', {
      class: 'seq-cell',
      type: 'button',
      'aria-label': `Track ${track.name || 'untitled'}, step ${index + 1}`,
      onclick: () =>
        editPattern('Toggle Step', (live) => {
          const target = live.tracks.find((entry) => entry.id === track.id);
          if (target === undefined) return;
          while (target.steps.length <= index) target.steps.push(offStepValue());
          const step = target.steps[index]!;
          const wasActive = step.isActive;
          // A loop track holds exactly one hit, so lighting a step *moves* the bed instead of
          // starting a second copy of it half a bar out of phase with the first. Anything
          // else would leave lit steps on the grid that the scheduler ignores, which is the
          // silent wrongness this whole mode exists to remove.
          if (target.oneHitPerPass === true) {
            for (let cursor = 0; cursor < target.steps.length; cursor++) target.steps[cursor]!.isActive = false;
          }
          step.isActive = !wasActive;
        }),
      oncontextmenu: (event: Event) => {
        event.preventDefault();
        const step = track.steps[index];
        if (step !== undefined) stepInspector(track.id, index, step);
      },
    }) as HTMLButtonElement;

  /**
   * Fits the strip to the pattern's length by adding to or trimming its tail.
   *
   * Cells the user can see are never replaced, which is the whole point: a scroller keeps its
   * offset across a mutation and loses it the moment its content is rebuilt.
   */
  const fitCells = (count: number): void => {
    while (cells.length > count) {
      const cell = cells.pop()!;
      disposeTree(cell);
      cell.remove();
    }
    for (let index = cells.length; index < count; index++) {
      const cell = makeCell(index);
      cells.push(cell);
      grid.appendChild(cell);
    }
  };

  /*
   * Three gestures land on the same 18 px cell: a tap toggles the step, a press that stays put
   * opens the inspector, and a drag scrolls a 32 or 64 step pattern sideways. Movement is what
   * tells them apart, so none of them needs the finger to be accurate.
   *
   * The state lives on the lane rather than on each cell. A touch pointer is implicitly captured
   * by the element it went down on, so a cell never sees `pointerleave` while the finger is still
   * dragging across it, and per-cell listeners had no way to notice the drag at all. One listener
   * on the lane sees the whole gesture.
   */
  const PAN_SLOP = 8;
  let pressTimer = 0;
  let startX = 0;
  let startY = 0;
  // Set by anything that has already spent the gesture: a pan, or the inspector opening under
  // the finger. The click that follows is the browser's, and it must not also edit the step.
  let claimed = false;

  const endPress = (): void => {
    window.clearTimeout(pressTimer);
    pressTimer = 0;
  };
  onCleanup(grid, endPress);

  onCleanup(
    grid,
    on(grid, 'pointerdown', (event: PointerEvent) => {
      endPress();
      claimed = false;
      startX = event.clientX;
      startY = event.clientY;
      const hit = event.target instanceof Element ? event.target.closest('.seq-cell') : null;
      const index = hit === null ? -1 : cells.indexOf(hit as HTMLButtonElement);
      if (index < 0) return;
      // Long-press opens the inspector; the same inspector is on the row's ⋯ button, so the
      // gesture is a shortcut rather than the only way in.
      pressTimer = window.setTimeout(() => {
        pressTimer = 0;
        claimed = true;
        const step = track.steps[index];
        if (step !== undefined) stepInspector(track.id, index, step);
      }, 480);
    }),
  );

  onCleanup(
    grid,
    on(grid, 'pointermove', (event: PointerEvent) => {
      if (claimed) return;
      const dx = Math.abs(event.clientX - startX);
      const dy = Math.abs(event.clientY - startY);
      if (dx < PAN_SLOP && dy < PAN_SLOP) return;
      // Past the slop the finger is travelling, not resting, so this is no longer a press.
      endPress();
      // Sideways it is a scroll and nothing else. Latching that decision is what stops a pan
      // across a 64 step pattern from rewriting the step it started on when the finger lifts.
      if (dx > dy) claimed = true;
    }),
  );

  onCleanup(grid, on(grid, 'pointerup', endPress));
  onCleanup(grid, on(grid, 'pointerleave', endPress));
  onCleanup(
    grid,
    on(grid, 'pointercancel', () => {
      // The browser has taken the gesture for its own scroll: same outcome as crossing the slop.
      endPress();
      claimed = true;
    }),
  );

  /*
   * Capture on the lane is the only place left to stop the toggle. Each cell's `onclick` is
   * registered by `h` before anything added here, and listeners on the target itself run in
   * registration order whatever their phase, so stopping the click on the cell is too late.
   */
  onCleanup(
    grid,
    on(
      grid,
      'click',
      (event: Event) => {
        if (!claimed) return;
        claimed = false;
        event.preventDefault();
        event.stopPropagation();
      },
      { capture: true },
    ),
  );

  const nameLabel = h('span', { class: 'seq-track-name truncate' });
  const noteLabel = h('span', { class: 'seq-track-note' });
  const muteButton = h(
    'button',
    {
      class: 'seq-flag',
      type: 'button',
      onclick: () =>
        editPattern('Mute Track', (live) => {
          const target = live.tracks.find((entry) => entry.id === track.id);
          if (target !== undefined) target.isMuted = !target.isMuted;
        }),
    },
    'M',
  );
  const soloButton = h(
    'button',
    {
      class: 'seq-flag',
      type: 'button',
      onclick: () =>
        editPattern('Solo Track', (live) => {
          const target = live.tracks.find((entry) => entry.id === track.id);
          if (target !== undefined) target.isSoloed = !target.isSoloed;
        }),
    },
    'S',
  );
  const menuButton = h(
    'button',
    { class: 'seq-flag', type: 'button', onclick: () => trackMenu(track, facts) },
    icon('slider.horizontal.3', { size: 14 }),
  );

  const row = h(
    'div',
    { class: 'seq-track' },
    h(
      'div',
      { class: 'seq-track-head' },
      h('div', { class: 'seq-track-titles' }, nameLabel, noteLabel),
      h('div', { class: 'seq-track-controls' }, muteButton, soloButton, menuButton),
    ),
    grid,
  );

  /** Everything in the row that is not a step. */
  const paintHead = (): void => {
    const pad = boundPad();
    const name = track.name === '' ? (pad?.name ?? 'Track') : track.name;
    nameLabel.textContent = name;
    const note = trackNote(facts);
    noteLabel.textContent = note;
    noteLabel.hidden = note === '';
    // The loop modifier changes nothing about the row's colour; it lets the note under the
    // name be styled as the aside it is, and marks the row for the a11y pass.
    row.classList.toggle('seq-track--loop', facts.oneHit);
    row.classList.toggle('seq-track--loop-warn', facts.padIsLoop && !facts.oneHit);
    row.style.setProperty('--track-tint', `var(--accent-${pad?.color ?? track.color})`);
    muteButton.classList.toggle('seq-flag--on', track.isMuted);
    muteButton.setAttribute('aria-label', `Mute ${name}`);
    soloButton.classList.toggle('seq-flag--solo', track.isSoloed);
    soloButton.setAttribute('aria-label', `Solo ${name}`);
    menuButton.setAttribute('aria-label', `Actions for ${name}`);
  };

  /** One pass over the strip, writing classes and custom properties only. */
  const paintCells = (): void => {
    const position = engine.transport.position.value;
    const playhead = position.isPlaying ? Math.floor(position.beats * pattern.stepsPerBeat) % Math.max(1, track.length) : -1;
    // The step a loop track actually fires on. Read from the same helper both schedulers use,
    // so a lit-but-inert step can never be drawn as the one that plays.
    const leading = facts.oneHit ? trackLeadingStepIndex(track) : -1;
    for (let index = 0; index < cells.length; index++) {
      const cell = cells[index]!;
      const step = track.steps[index];
      const active = step?.isActive === true;
      const inert = facts.oneHit && active && index !== leading;
      cell.classList.toggle('seq-cell--on', active);
      cell.classList.toggle('seq-cell--inert', inert);
      cell.classList.toggle('seq-cell--detail', active && step !== undefined && stepHasDetail(step));
      cell.classList.toggle('seq-cell--beat', index % pattern.stepsPerBeat === 0);
      cell.classList.toggle('seq-cell--past', index >= track.length);
      cell.classList.toggle('seq-cell--playhead', index === playhead);
      cell.style.setProperty('--step-velocity', String(step?.velocity ?? 0));
      cell.setAttribute('aria-pressed', active ? 'true' : 'false');
      // Dimming says "lit but silent" to a sighted user; this says it to everyone else.
      const base = `Track ${track.name || 'untitled'}, step ${index + 1}`;
      cell.setAttribute(
        'aria-label',
        !facts.oneHit
          ? base
          : inert
            ? `${base}, lit but silent: this loop plays once per pass`
            : index === leading
              ? `${base}, where the loop starts`
              : base,
      );
    }
  };

  fitCells(pattern.length);
  paintHead();

  const stop = effect(() => {
    revision.value;
    paintCells();
  });
  onCleanup(row, stop);

  return {
    el: row,
    sync: (nextTrack: Track, nextPattern: Pattern) => {
      track = nextTrack;
      pattern = nextPattern;
      // `sync` is called from the track list's effect. Without this the transport position read
      // inside `paintCells` would become a dependency of *that* effect, and the whole list would
      // reconcile on every sixteenth note.
      untracked(() => {
        facts = trackLoopFacts(track, pattern, boundPad());
        fitCells(pattern.length);
        paintHead();
        paintCells();
      });
    },
    destroy: () => {
      disposeTree(row);
    },
  };
}

/**
 * Turns one-hit-per-pass on or off for a track.
 *
 * Turning it on is instant — it can only make a track sound more like what the grid shows.
 * Turning it *off* on a loop is the destructive direction, so it is explained first: every
 * lit step will start another copy of the same bar, and copies that start half a bar apart
 * do not line up again. The user is allowed to do it; they are not allowed to do it by
 * accident and then wonder why the pattern smears.
 */
// `enabled`, not `on`: `on` is the DOM helper this file now uses for the lane's gesture
// listeners, and a parameter that shadows it inside this function would read as that.
function setOneHitPerPass(track: Track, enabled: boolean): void {
  editPattern(enabled ? 'Loop Track' : 'Every Step Track', (pattern) => {
    const live = pattern.tracks.find((entry) => entry.id === track.id);
    if (live === undefined) return;
    if (enabled) {
      live.oneHitPerPass = true;
      // Collapse to the earliest lit step so what is drawn and what sounds agree from the
      // moment the mode is switched on.
      const leading = trackLeadingStepIndex(live);
      for (let index = 0; index < live.steps.length; index++) {
        live.steps[index]!.isActive = index === leading && leading >= 0;
      }
    } else delete live.oneHitPerPass;
  });
  if (enabled) notify('Plays once per pass', 'The bed restarts at the top of every pass, from its one lit step.');
}

function trackMenu(track: Track, facts: TrackLoopFacts): void {
  const padID = track.padID;
  const open = project.peek();
  const assetID = open === null || padID === undefined ? undefined : projectPad(open, padID)?.assetID;
  presentMenu(track.name === '' ? 'Track' : track.name, [
    {
      title: facts.oneHit ? 'Play every lit step' : 'Play once per pass',
      symbol: 'arrow.trianglehead.2.clockwise',
      // 20 characters and 10, one line each. The 39- and 55-character details they replace
      // wrapped to two and three lines against a one-line title. The sentence they were
      // carrying is now the note under this menu's own title, where a sentence fits.
      detail: facts.oneHit ? 'Ordinary steps again' : 'Loops only',
      onSelect: () => {
        if (!facts.oneHit) {
          setOneHitPerPass(track, true);
          return;
        }
        if (!facts.padIsLoop) {
          setOneHitPerPass(track, false);
          return;
        }
        confirmDialog({
          title: 'Play every lit step?',
          message:
            'This sound is a loop: a whole bar or more of music. Every lit step starts another copy, and two copies half a bar apart never line up again. Slicing the loop into pads is the usual way to put it on a grid.',
          confirmTitle: 'Play every step',
          destructive: true,
          onConfirm: () => setOneHitPerPass(track, false),
        }).present();
      },
    },
    ...(facts.padIsLoop && assetID !== undefined
      ? [
          {
            title: 'Slice this loop into pads',
            symbol: 'rectangle.split.3x1.fill',
            detail: 'A pad per hit',
            onSelect: () => presentLoopSlicer(assetID),
          },
        ]
      : []),
    {
      title: 'Clear this track',
      symbol: 'trash.fill',
      onSelect: () =>
        editPattern('Clear Track', (pattern) => {
          const live = pattern.tracks.find((entry) => entry.id === track.id);
          if (live === undefined) return;
          for (let index = 0; index < live.steps.length; index++) live.steps[index] = offStepValue();
        }),
    },
    {
      title: 'Copy steps',
      symbol: 'square.grid.3x3.fill',
      onSelect: () => {
        stepClipboard.value = track.steps.map((step) => structuredClone(step));
        notify('Steps copied', `${track.steps.length} steps`);
      },
    },
    {
      title: 'Paste steps',
      symbol: 'square.and.arrow.up',
      disabled: stepClipboard.peek() === null,
      onSelect: () => {
        const copied = stepClipboard.peek();
        if (copied === null) return;
        editPattern('Paste Steps', (pattern) => {
          const live = pattern.tracks.find((entry) => entry.id === track.id);
          if (live === undefined) return;
          live.steps = copied.map((step) => structuredClone(step));
        });
      },
    },
    {
      title: `Track length: ${track.length}`,
      detail: 'Shorter tracks create polyrhythms',
      symbol: 'arrow.left.and.right',
      onSelect: () =>
        presentMenu('Track length', PATTERN_LENGTH_OPTIONS.map((length) => ({
          title: `${length} steps`,
          onSelect: () =>
            editPattern('Track Length', (pattern) => {
              const live = pattern.tracks.find((entry) => entry.id === track.id);
              if (live === undefined) return;
              live.length = length;
              while (live.steps.length < length) live.steps.push(offStepValue());
            }),
        }))),
    },
    {
      title: 'Bounce this track to a sound',
      symbol: 'waveform.path',
      onSelect: () => void bounceTrack(track),
    },
    {
      title: 'Remove track',
      symbol: 'trash.fill',
      destructive: true,
      onSelect: () =>
        editPattern('Remove Track', (pattern) => {
          pattern.tracks = pattern.tracks.filter((entry) => entry.id !== track.id);
        }),
    },
  ], facts.padIsLoop ? 'A loop track fires once per pass, not once per step.' : undefined);
}

async function bounceTrack(track: Track): Promise<void> {
  const open = project.peek();
  const pattern = currentPattern.peek();
  if (open === null || pattern === null || track.padID === undefined) return;
  try {
    const audio = await renderPattern(open, {
      patternID: pattern.id,
      isolatePadID: track.padID,
      repeats: 1,
      media: {
        load: async (asset) => {
          const buffer = await bufferForAsset(asset);
          if (buffer === null) throw new Error(`“${asset.name}” could not be loaded.`);
          return buffer;
        },
      },
    });
    const asset = await saveRenderedAsAsset(audio, `${track.name || 'Track'} bounce`, 'renderedBounce', ['bounce']);
    notify('Bounced', `“${asset.name}” is in your library.`);
  } catch (error) {
    reportFailure('Bounce failed', error);
  }
}

export function sequencerScreen(): HTMLElement {
  if (project.peek() === null) return requiresProject();

  /**
   * The track list, reconciled by track id rather than rebuilt.
   *
   * This host is the one horizontal scroller the whole grid shares, so emptying it on every
   * revision collapsed its content to nothing and the browser clamped the offset to zero: a
   * user editing bar 4 was thrown back to bar 1 by their own edit. The discarded rows also kept
   * their effects running, because `replaceChildren` does not release bindings. A row is created
   * once, re-pointed on every edit, and torn down only when its track is genuinely gone.
   */
  const tracksHost = h('div', { class: 'seq-tracks' });
  const rows = new Map<string, TrackRowHandle>();
  let ruler: StepRulerHandle | null = null;
  // Built once and parked: it is the same element every time it is wanted.
  const noTracks = emptyState({
    symbol: 'music.note.list',
    title: 'No tracks yet',
    message: 'Add a track for any loaded pad, or arm recording and play the board. Hits are written straight in.',
    actionTitle: 'Add a track',
    onAction: () => addTrack(),
  });

  const stopTracks = effect(() => {
    revision.value;
    const pattern = currentPattern.value;
    const tracks = pattern?.tracks ?? [];

    const living = new Set(tracks.map((track) => track.id));
    for (const [id, handle] of [...rows]) {
      if (living.has(id)) continue;
      handle.destroy();
      handle.el.remove();
      rows.delete(id);
    }

    const isEmpty = pattern !== null && tracks.length === 0;
    if (isEmpty && noTracks.parentNode === null) tracksHost.appendChild(noTracks);
    else if (!isEmpty && noTracks.parentNode !== null) noTracks.remove();
    if (pattern === null || tracks.length === 0) {
      ruler?.el.remove();
      return;
    }

    // The ruler is row zero of the same grid, so it is fitted before the rows are placed.
    if (ruler === null) ruler = stepRuler(pattern);
    else ruler.sync(pattern);
    if (ruler.el.parentNode === null) tracksHost.insertBefore(ruler.el, tracksHost.firstChild);

    // Walk the wanted order against the live children, moving only what is out of place.
    let cursor: ChildNode | null = ruler.el.nextSibling;
    for (const track of tracks) {
      let handle = rows.get(track.id);
      if (handle === undefined) {
        handle = trackRow(track, pattern);
        rows.set(track.id, handle);
      } else {
        handle.sync(track, pattern);
      }
      if (cursor === handle.el) cursor = cursor.nextSibling;
      else tracksHost.insertBefore(handle.el, cursor);
    }
  });
  onCleanup(tracksHost, () => {
    stopTracks();
    for (const handle of rows.values()) handle.destroy();
    rows.clear();
  });

  function addTrack(): void {
    const open = project.peek();
    if (open === null) return;
    const loaded = open.banks.flatMap((bank) => bank.pads).filter((pad) => !padIsEmpty(pad));
    if (loaded.length === 0) {
      notify('No sounds loaded', 'Assign a sound to a pad first.');
      return;
    }
    const offersLoop = loaded.some((pad) => projectPadPlaysLoop(open, pad));
    presentMenu(
      'Add a track for…',
      loaded.map((pad) => {
        const isLoop = projectPadPlaysLoop(open, pad);
        const asset = pad.assetID === undefined ? undefined : projectAsset(open, pad.assetID);
        const bars = asset === undefined ? 0 : loopBarLength(asset, open.tempo, open.timeSignatureBeats, pad.edit.playbackRate);
        return {
          title: pad.name === '' ? `Pad ${pad.slot + 1}` : pad.name,
          // A loop says so in the picker, before the row exists. The alternative is a user
          // adding a break, drawing four steps on it, and finding out by ear.
          symbol: isLoop ? 'arrow.trianglehead.2.clockwise' : 'square.grid.3x3.fill',
          // 15 characters, one line. The old 34-character detail wrapped to two lines against
          // a one-line title; the fact it was carrying is in the note under the menu's title,
          // where a sentence fits.
          ...(isLoop ? { detail: `Loop · ${formatBars(bars)}` } : {}),
          onSelect: () => {
            editPattern('Add Track', (pattern) => {
              const track = makeTrack({
                padID: pad.id,
                name: pad.name,
                length: pattern.length,
                color: pad.color,
                // A loop lands as a loop track. It can be turned off in the track's own
                // menu, with the explanation of what that means attached to the switch.
                oneHitPerPass: isLoop,
              });
              while (track.steps.length < pattern.length) track.steps.push(offStepValue());
              // A loop bed with no lit step is silent and looks broken, so the pass starts it
              // at the top — the only phase that lines up with the bar.
              if (isLoop) track.steps[0]!.isActive = true;
              pattern.tracks.push(track);
            });
            if (isLoop) {
              notify('Loop track added', 'It plays once per pass, from step 1. Slice it if you want it on the grid.');
            }
          },
        };
      }),
      offersLoop ? 'A loop track fires once per pass, not once per step.' : undefined,
    );
  }

  const patternRail = h('div', { class: 'chip-row scroll-x' });
  const stopPatterns = effect(() => {
    revision.value;
    const open = project.value;
    patternRail.replaceChildren();
    for (const pattern of open?.patterns ?? []) {
      patternRail.appendChild(
        chip({
          // A queued chip says so, so "it did not change" and "it will change at the bar
          // line" are never the same picture.
          title: computed(() => (sequencer.queuedPatternID.value === pattern.id ? `${pattern.name} · queued` : pattern.name)),
          selected: computed(() => currentPattern.value?.id === pattern.id),
          onPress: () => sequencer.queuePattern(pattern.id),
        }).el,
      );
    }
    patternRail.appendChild(
      chip({
        title: '+ Pattern',
        onPress: () =>
          mutate('Add Pattern', (target) => {
            const source = currentPattern.peek();
            const created = makePattern(`Pattern ${target.patterns.length + 1}`, {
              length: source?.length ?? 16,
              stepsPerBeat: source?.stepsPerBeat ?? 4,
            });
            target.patterns.push(created);
            currentPatternID.value = created.id;
          }),
      }).el,
    );
  });
  onCleanup(patternRail, stopPatterns);

  // The rail was chips and nothing else, so nothing told a first-time reader that a pattern is
  // a section of the song, as opposed to a bank, which is a page of pads. An `h2` because every
  // other kicker on this screen is one, and the rotor should list Patterns beside Steps.
  const patternBlock = h(
    'div',
    { class: 'pattern-rail' },
    h('h2', { class: 'kicker' }, 'Patterns'),
    patternRail,
  );

  const sceneHost = h('div', { class: 'scene-grid' });
  list(
    sceneHost,
    computed(() => {
      revision.value;
      return project.value?.scenes ?? [];
    }),
    (scene) => scene.id,
    (scene) =>
      h(
        'button',
        {
          class: 'scene-card',
          type: 'button',
          'aria-label': `Launch scene ${scene.name}`,
          onclick: () => sequencer.launchScene(scene),
          oncontextmenu: (event: Event) => {
            event.preventDefault();
            sceneMenu(scene);
          },
        },
        h(
          'p',
          { class: 'kicker' },
          computed(() => (sequencer.queuedSceneID.value === scene.id ? 'Queued' : 'Scene')),
        ),
        h('span', { class: 'truncate' }, scene.name),
      ),
  );


  const songHost = h('div', { class: 'song-list' });
  const stopSong = effect(() => {
    revision.value;
    const open = project.value;
    songHost.replaceChildren();
    const sections = open?.song.sections ?? [];
    if (sections.length === 0) {
      songHost.appendChild(h('p', { class: 'muted' }, 'Chain patterns here to build an arrangement.'));
      return;
    }
    sections.forEach((entry, index) => {
      const pattern = open?.patterns.find((candidate) => candidate.id === entry.patternID);
      songHost.appendChild(
        h(
          'div',
          { class: 'song-row' },
          h('span', { class: 'kicker' }, `${index + 1}`),
          h('span', { class: 'truncate' }, pattern?.name ?? 'Missing pattern'),
          valueStepper({
            label: 'Repeats',
            value: entry.repeatCount,
            min: 1,
            max: 32,
            onChange: (value) =>
              mutate('Song Repeats', (target) => {
                const live = target.song.sections[index];
                if (live !== undefined) live.repeatCount = Math.round(value);
              }),
          }).el,
          iconButton({
            symbol: 'trash.fill',
            label: 'Remove section',
            onPress: () =>
              mutate('Remove Section', (target) => {
                target.song.sections.splice(index, 1);
              }),
          }).el,
        ),
      );
    });
  });
  onCleanup(songHost, stopSong);

  const tempoStepper = valueStepper({
    label: 'Tempo',
    value: computed(() => {
      revision.value;
      return project.value?.tempo ?? 96;
    }),
    min: 20,
    max: 300,
    unit: 'BPM',
    onChange: (value) => mutate('Tempo', (open) => (open.tempo = Math.round(value)), { coalesceKey: 'tempo' }),
  });

  const swingStepper = valueStepper({
    label: 'Swing',
    value: computed(() => {
      revision.value;
      return Math.round((project.value?.swing ?? 0) * 100);
    }),
    min: 0,
    max: 75,
    unit: '%',
    onChange: (value) => mutate('Swing', (open) => (open.swing = value / 100), { coalesceKey: 'swing' }),
  });

  const lengthRail = segmentedRail({
    items: PATTERN_LENGTH_OPTIONS.map((length) => ({ id: String(length), title: `${length}` })),
    selection: computed(() => String(currentPattern.value?.length ?? 16)),
    label: 'Pattern length',
    onSelect: (id) =>
      editPattern('Pattern Length', (pattern) => {
        const length = Number.parseInt(id, 10);
        const previous = pattern.length;
        pattern.length = length;
        for (const track of pattern.tracks) {
          // Only tracks that were following the pattern follow it now. Writing `length` onto
          // every track destroyed every polyrhythm in the pattern in one tap, silently and
          // with the per-track length control sitting a few rows below.
          if (track.length === previous) track.length = length;
          while (track.steps.length < track.length) track.steps.push(offStepValue());
        }
      }),
  });

  const recordRail = segmentedRail({
    items: [
      { id: 'off', title: 'Off' },
      { id: 'overdub', title: 'Overdub' },
      { id: 'replace', title: 'Replace' },
    ],
    selection: computed(() => sequencer.recordMode.value),
    label: 'Performance recording',
    onSelect: (id) => {
      sequencer.recordMode.value = id as 'off' | 'overdub' | 'replace';
    },
  });

  /**
   * Slicing, offered on the screen where the mismatch actually bites.
   *
   * A loop cannot be programmed on a step grid, and the fix has always existed — cut it into
   * pads and every step is a one-shot again. It was reachable from nowhere, which is the same
   * as not existing. The button is only here when the project has a loop to cut.
   */
  const loopCount = computed(() => {
    revision.value;
    const open = project.value;
    return open === null ? 0 : projectLoopAssets(open).length;
  });

  const sliceButton = neonButton({
    title: 'Slice a loop into pads',
    style: 'secondary',
    onPress: () => {
      const open = project.peek();
      if (open === null) return;
      // The loop already on the grid is the one the user is looking at; otherwise the newest.
      const onGrid = currentPattern
        .peek()
        ?.tracks.map((track) => (track.padID === undefined ? undefined : projectPad(open, track.padID)))
        .find((pad) => pad !== undefined && projectPadPlaysLoop(open, pad));
      presentLoopSlicer(onGrid?.assetID ?? projectLoopAssets(open)[0]?.id);
    },
  });

  const loopHint = h('p', { class: 'muted' });
  const stopLoopHint = effect(() => {
    const count = loopCount.value;
    sliceButton.el.hidden = count === 0;
    loopHint.hidden = count === 0;
    loopHint.textContent =
      count === 0
        ? ''
        : 'A loop is a bed: it spans the pattern rather than sitting in a step, so its track plays once per pass. Slice it and every hit becomes a pad the grid can programme.';
  });
  onCleanup(tracksHost, stopLoopHint);

  const tapHint = signal('Tap 4 times');
  const screen = h(
    'div',
    { class: 'screen screen--sequencer' },
    screenHeader({
      kicker: computed(() => {
        revision.value;
        return currentPattern.value?.name ?? 'Pattern';
      }),
      title: 'Sequencer',
      showsBack: false,
      actions: [
        iconButton({ symbol: 'square.and.arrow.up', label: 'Export', onPress: () => void navigate({ name: 'export', projectID: project.peek()?.id }) }).el,
        iconButton({
          symbol: 'slider.horizontal.3',
          label: 'Pattern actions',
          onPress: () => patternMenu(),
        }).el,
      ],
    }),
    screenBody(
      patternBlock,
      section(
        'Steps',
        lengthRail.el,
        tracksHost,
        h(
          'div',
          { class: 'seq-track-actions' },
          neonButton({ title: 'Add track', style: 'secondary', onPress: addTrack }).el,
          sliceButton.el,
        ),
        loopHint,
      ),
      // Everything on this screen that is not the grid, in one box. Landscape needs a single
      // element to place beside the instrument. `display: contents` would have done it without
      // a wrapper, but the sections would then no longer match `.screen-body > *` and would
      // lose the no-shrink rule that keeps them from compressing in the portrait scroller.
      h(
        'div',
        { class: 'seq-aux' },
        section(
          'Timing',
          h(
            'div',
            { class: 'control-grid' },
            tempoStepper.el,
            swingStepper.el,
            neonButton({
              title: computed(() => tapHint.value),
              style: 'secondary',
              onPress: () => {
                const bpm = engine.transport.tapTempo();
                if (bpm === null) {
                  tapHint.value = 'Keep tapping…';
                  return;
                }
                tapHint.value = `${Math.round(bpm)} BPM`;
                mutate('Tap Tempo', (open) => (open.tempo = Math.round(bpm)));
              },
            }).el,
          ),
          toggle({
            title: 'Metronome',
            subtitle: 'Clicks on every beat while the transport runs',
            on: computed(() => {
              revision.value;
              return project.value?.metronome.isEnabled ?? false;
            }),
            onChange: (on) => mutate('Metronome', (open) => (open.metronome.isEnabled = on)),
          }).el,
          toggle({
            title: 'Count-in',
            subtitle: 'One bar of clicks before recording starts',
            on: computed(() => settings.value.countInEnabled),
            onChange: (on) => updateSettings({ countInEnabled: on }),
          }).el,
        ),
        section(
          'Record what you play',
          recordRail.el,
          toggle({
            title: 'Record knob moves',
            subtitle: 'Captures parameter sweeps into this pattern while it plays',
            on: computed(() => sequencer.automationArmed.value),
            onChange: (on) => {
              sequencer.automationArmed.value = on;
            },
          }).el,
        ),
        section(
          'Scenes',
          sceneHost,
          h('p', { class: 'muted' }, 'A scene launches its pattern at the next bar line and re-holds the pads it was captured with.'),
          neonButton({
            title: 'Capture a scene from what is playing',
            style: 'secondary',
            onPress: () => captureScene(),
          }).el,
        ),
        section(
          'Song',
          toggle({
            title: 'Play the arrangement',
            subtitle: 'Chains the sections below instead of looping one pattern',
            on: computed(() => sequencer.isSongMode.value),
            onChange: (on) => {
              sequencer.isSongMode.value = on;
              mutate('Song Mode', (open) => (open.song.isEnabled = on), { silent: true });
            },
          }).el,
          songHost,
          neonButton({
            title: 'Add this pattern to the song',
            style: 'secondary',
            onPress: () =>
              mutate('Add Section', (open) => {
                const patternID = currentPattern.peek()?.id;
                if (patternID === undefined) return;
                open.song.sections.push(makeSongSection(patternID, { repeatCount: 2 }));
              }),
          }).el,
        ),
      ),
    ),
  );

  function patternMenu(): void {
    const pattern = currentPattern.peek();
    if (pattern === null) return;
    const open = project.peek();
    const loops = open === null ? [] : projectLoopAssets(open);
    presentMenu(pattern.name, [
      ...(loops.length === 0
        ? []
        : [
            {
              title: 'Slice a loop into pads',
              symbol: 'rectangle.split.3x1.fill',
              detail: `${loops.length} loop${loops.length === 1 ? '' : 's'} in this project`,
              onSelect: () => presentLoopSlicer(loops[0]?.id),
            },
          ]),
      {
        title: 'Rename pattern',
        symbol: 'mouth.fill',
        onSelect: () =>
          presentPrompt({ title: 'Pattern name', value: pattern.name, onConfirm: (name) => editPattern('Rename Pattern', (live) => (live.name = name)) }),
      },
      {
        title: 'Duplicate pattern',
        symbol: 'square.grid.3x3.fill',
        onSelect: () =>
          mutate('Duplicate Pattern', (open) => {
            const copy = structuredClone(pattern);
            copy.id = newUUID();
            copy.name = `${pattern.name} copy`;
            for (const track of copy.tracks) track.id = newUUID();
            open.patterns.push(copy);
            currentPatternID.value = copy.id;
          }),
      },
      {
        title: 'Quantise micro-timing',
        symbol: 'clock.fill',
        detail: 'Snaps every recorded hit back to the grid',
        onSelect: () =>
          editPattern('Quantise Pattern', (live) => {
            for (const track of live.tracks) for (const step of track.steps) step.microTiming = 0;
          }),
      },
      {
        title: 'Clear pattern',
        symbol: 'trash.fill',
        destructive: true,
        onSelect: () => {
          confirmDialog({
            title: 'Clear every track?',
            message: 'The tracks stay; only the steps are cleared. Undo brings them back.',
            confirmTitle: 'Clear',
            destructive: true,
            onConfirm: () => {
              editPattern('Clear Pattern', (live) => {
                for (const track of live.tracks) {
                  for (let index = 0; index < track.steps.length; index++) track.steps[index] = offStepValue();
                }
              });
              notifyUndoable('Pattern cleared', 'Every track kept its lane; the steps are gone.');
            },
          }).present();
        },
      },
      // Clearing a pattern was here and deleting it was nowhere, so a pattern could be emptied
      // but never got rid of. Slicing a loop creates one automatically, named after the sample,
      // so they accumulate exactly like banks did.
      ...(patternCount() > 1
        ? [
            {
              title: 'Delete pattern',
              symbol: 'trash.fill',
              destructive: true,
              detail: 'Removes its tracks and steps',
              onSelect: () => confirmDeletePattern(),
            },
          ]
        : []),
    ]);
  }

  function patternCount(): number {
    return project.peek()?.patterns.length ?? 0;
  }

  /**
   * Deletes the open pattern, and everything that pointed at it.
   *
   * A pattern owns its tracks and automation, so those go with it. What does not live inside it
   * are the references held elsewhere: a scene launches a pattern by id, and a song arrangement
   * is a list of sections each naming one. Leaving those behind would give you scenes that
   * launch nothing and a song that plays a gap, so the scene loses its binding and the sections
   * are removed. All of it inside one mutate, so undo restores the whole thing together.
   */
  function confirmDeletePattern(): void {
    const open = project.peek();
    const pattern = currentPattern.peek();
    if (open === null || pattern === null || open.patterns.length <= 1) return;

    const steps = pattern.tracks.reduce(
      (total, track) => total + track.steps.filter((step) => step.isActive).length,
      0,
    );
    const scenes = open.scenes.filter((scene) => scene.patternID === pattern.id).length;
    const sections = open.song.sections.filter((entry) => entry.patternID === pattern.id).length;

    const also: string[] = [];
    if (scenes > 0) also.push(`${scenes} scene${scenes === 1 ? '' : 's'} will lose its pattern`);
    if (sections > 0) also.push(`${sections} song section${sections === 1 ? '' : 's'} will be removed`);

    confirmDialog({
      title: `Delete “${pattern.name}”?`,
      message: [
        `This removes ${pattern.tracks.length} track${pattern.tracks.length === 1 ? '' : 's'} and ${steps} step${steps === 1 ? '' : 's'}.`,
        ...also,
        'Your sounds stay in the library, and this can be undone.',
      ].join(' '),
      confirmTitle: 'Delete pattern',
      destructive: true,
      onConfirm: () => {
        const removedID = pattern.id;
        mutate('Delete Pattern', (target) => {
          if (target.patterns.length <= 1) return;
          const index = target.patterns.findIndex((entry) => entry.id === removedID);
          if (index < 0) return;
          target.patterns.splice(index, 1);
          for (const scene of target.scenes) {
            if (scene.patternID === removedID) delete scene.patternID;
          }
          target.song.sections = target.song.sections.filter((entry) => entry.patternID !== removedID);
        });
        // Land on a pattern that still exists, or the sequencer renders nothing.
        const remaining = project.peek()?.patterns ?? [];
        currentPatternID.value = remaining[0]?.id ?? null;
        notifyUndoable('Pattern deleted', 'Its tracks and steps went with it.');
      },
    }).present();
  }

  onCleanup(screen, () => {
    tempoStepper.destroy();
    swingStepper.destroy();
    lengthRail.destroy();
    recordRail.destroy();
    sliceButton.destroy();
  });
  return screen;
}
